import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ClipboardList,
  Flag,
  LayoutGrid,
  Pencil,
  Plus,
  ShieldAlert,
  Users,
} from 'lucide-react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox, Select, Textarea } from '@/components/ui/misc';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/features/auth/auth-layout';
import { useUserOptions } from '@/hooks/use-user-options';
import { usePermission } from '@/hooks/use-permission';
import { ApiRequestError } from '@/lib/api';
import { formatDate, formatMoney } from '@/lib/format';
import { cn } from '@/lib/utils';
import {
  useCloseProject,
  useDecideChangeRequest,
  useProfitability,
  useProject,
  useProjectSummary,
  useSaveChangeRequest,
  useSaveMilestone,
  useSaveRisk,
  useSetMilestoneStatus,
  useSetProjectMembers,
} from './api';
import { ProjectDialog } from './project-dialog';
import { SprintsPanel } from './sprints-panel';
import {
  BILLING_MODEL_LABELS,
  BILLING_TRIGGER_LABELS,
  HEALTH_DOT,
  HEALTH_LABELS,
  MILESTONE_STATUS_LABELS,
  MILESTONE_STATUS_VARIANT,
  PROJECT_STATUS_LABELS,
  PROJECT_STATUS_VARIANT,
  PROJECT_TYPE_LABELS,
  RISK_STATUS_LABELS,
} from './labels';
import type { Milestone, Project } from './types';

const TABS = [
  ['overview', 'Overview'],
  ['milestones', 'Milestones'],
  ['changes', 'Change requests'],
  ['risks', 'Risks'],
  ['sprints', 'Sprints'],
  ['team', 'Team'],
  ['money', 'Profitability'],
] as const;

/** The closure checklist, matching the server's list exactly (spec PRJ-08). */
const CLOSURE_ITEMS: [string, string][] = [
  ['allTasksClosed', 'All work items closed or moved out'],
  ['finalInvoiceRaised', 'Final invoice raised'],
  ['documentsArchived', 'Documents archived'],
  ['handoverCompleted', 'Handover completed'],
  ['warrantyStartSet', 'Warranty or AMC start date set'],
  ['retrospectiveRecorded', 'Retrospective recorded'],
];

export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as (typeof TABS)[number][0]) ?? 'overview';
  const [editing, setEditing] = useState(false);
  const [closing, setClosing] = useState(false);

  const { data: project, isLoading } = useProject(projectId);
  const { data: summary } = useProjectSummary(projectId);

  if (isLoading) return <TableSkeleton columns={4} />;
  if (!project) return null;

  return (
    <div>
      <PageHeader
        breadcrumbs={[
          { label: 'Projects', to: '/delivery/projects' },
          { label: project.name },
        ]}
        title={
          <span className="flex items-center gap-2.5">
            <span className={cn('h-2.5 w-2.5 rounded-full', HEALTH_DOT[project.health])} />
            {project.name}
          </span>
        }
        description={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-mono text-xs">{project.code}</span>
            <span>·</span>
            <span>{project.company.name}</span>
            <span>·</span>
            <span>{PROJECT_TYPE_LABELS[project.projectType]}</span>
            <span>·</span>
            <span>{BILLING_MODEL_LABELS[project.billingModel]}</span>
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <Badge variant={PROJECT_STATUS_VARIANT[project.status]}>
              {PROJECT_STATUS_LABELS[project.status]}
            </Badge>
            <Button asChild variant="outline" size="sm">
              <Link to={`/delivery/board?projectId=${project.id}`}>
                <LayoutGrid className="h-4 w-4" />
                Board
              </Link>
            </Button>
            <Can permission="project.project.update">
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                <Pencil className="h-4 w-4" />
                Edit
              </Button>
              {project.status !== 'COMPLETED' && (
                <Button variant="outline" size="sm" onClick={() => setClosing(true)}>
                  <CheckCircle2 className="h-4 w-4" />
                  Close project
                </Button>
              )}
            </Can>
          </div>
        }
      />

      <div className="mb-5 flex flex-wrap gap-1 border-b">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setParams({ tab: key })}
            className={
              tab === key
                ? 'border-b-2 border-primary px-3 py-2 text-sm font-medium'
                : 'px-3 py-2 text-sm text-muted-foreground hover:text-foreground'
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <Overview project={project} summary={summary} />}
      {tab === 'milestones' && <Milestones project={project} />}
      {tab === 'changes' && <ChangeRequests project={project} />}
      {tab === 'risks' && <Risks project={project} />}
      {tab === 'sprints' && <SprintsPanel projectId={project.id} />}
      {tab === 'team' && <Team project={project} />}
      {tab === 'money' && <Money project={project} />}

      {editing && <ProjectDialog project={project} onClose={() => setEditing(false)} />}
      {closing && <CloseDialog project={project} onClose={() => setClosing(false)} />}
    </div>
  );
}

function Overview({
  project,
  summary,
}: {
  project: Project;
  summary?: ReturnType<typeof useProjectSummary>['data'];
}) {
  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          label="Contract value"
          value={formatMoney(project.contractValue, project.currency)}
          hint={`${project.milestoneCount} milestones`}
        />
        <Stat
          label="Progress"
          value={`${summary?.percentComplete ?? 0}%`}
          hint={`${summary?.doneItems ?? 0} of ${summary?.totalItems ?? 0} work items done`}
        />
        <Stat
          label="Budget"
          value={project.budgetHours > 0 ? `${project.budgetHours}h` : '—'}
          hint={project.budgetHours > 0 ? 'Logged time is on the profitability tab' : 'No budget set'}
        />
        <Stat
          label="Target end"
          value={project.targetEndDate ? formatDate(project.targetEndDate) : '—'}
          hint={project.startDate ? `Started ${formatDate(project.startDate)}` : 'Not started'}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Where it stands</CardTitle>
            <CardDescription>
              Progress is work items done over work items total — the only measure held here that
              nobody has to type in.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Row label="Health" value={HEALTH_LABELS[project.health]} />
            <Row label="Current phase" value={summary?.currentPhase?.name ?? 'None in progress'} />
            <Row
              label="Milestones"
              value={`${summary?.milestonesComplete ?? 0} of ${summary?.milestonesTotal ?? 0} complete`}
            />
            <Row
              label="Open risks"
              value={String(summary?.openRisks ?? 0)}
              tone={summary?.openRisks ? 'warning' : undefined}
            />
            <Row label="Manager" value={project.manager?.name ?? 'Unassigned'} />
            {project.deal && (
              <Row
                label="From deal"
                value={
                  <Link to={`/crm/deals/${project.deal.id}`} className="text-primary hover:underline">
                    {project.deal.number} {project.deal.title}
                  </Link>
                }
              />
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Coming up</CardTitle>
            <CardDescription>Milestones that have not been signed off yet.</CardDescription>
          </CardHeader>
          <CardContent>
            {!summary?.upcomingMilestones.length ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                Nothing outstanding.
              </p>
            ) : (
              <ul className="space-y-3">
                {summary.upcomingMilestones.map((milestone) => (
                  <li key={milestone.id}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{milestone.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {milestone.plannedEnd ? formatDate(milestone.plannedEnd) : 'No date'}
                      </span>
                    </div>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary"
                        style={{ width: `${milestone.percentComplete}%` }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {project.description && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Description</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="whitespace-pre-line text-sm text-muted-foreground">
              {project.description}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Milestones({ project }: { project: Project }) {
  const save = useSaveMilestone(project.id);
  const setStatus = useSetMilestoneStatus(project.id);
  const [adding, setAdding] = useState(false);
  const [signingOff, setSigningOff] = useState<Milestone | null>(null);
  const [form, setForm] = useState({
    name: '',
    deliverables: '',
    plannedEnd: '',
    billingAmount: '',
    billingTrigger: 'ON_COMPLETION',
  });

  const milestones = project.milestones ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Can permission="project.milestone.create">
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" />
            Add milestone
          </Button>
        </Can>
      </div>

      {adding && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Planned end</Label>
                <Input
                  type="date"
                  value={form.plannedEnd}
                  onChange={(e) => setForm({ ...form, plannedEnd: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Deliverables</Label>
              <Textarea
                rows={2}
                value={form.deliverables}
                onChange={(e) => setForm({ ...form, deliverables: e.target.value })}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Billing amount</Label>
                <Input
                  inputMode="decimal"
                  placeholder="0.00"
                  value={form.billingAmount}
                  onChange={(e) => setForm({ ...form, billingAmount: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Bills when</Label>
                <Select
                  value={form.billingTrigger}
                  onChange={(e) => setForm({ ...form, billingTrigger: e.target.value })}
                >
                  {Object.entries(BILLING_TRIGGER_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={save.isPending || form.name.trim().length < 2}
                onClick={async () => {
                  await save.mutateAsync({
                    name: form.name,
                    deliverables: form.deliverables || undefined,
                    plannedEnd: form.plannedEnd || undefined,
                    billingAmount: form.billingAmount || undefined,
                    billingTrigger: form.billingTrigger,
                    displayOrder: milestones.length,
                  });
                  setAdding(false);
                  setForm({
                    name: '',
                    deliverables: '',
                    plannedEnd: '',
                    billingAmount: '',
                    billingTrigger: 'ON_COMPLETION',
                  });
                }}
              >
                Save milestone
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {milestones.length === 0 && !adding ? (
        <Card>
          <CardContent className="py-10 text-center">
            <Flag className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No milestones yet. On a milestone-billed project, these are what release invoicing.
            </p>
          </CardContent>
        </Card>
      ) : (
        milestones.map((milestone) => (
          <Card key={milestone.id}>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-medium">{milestone.name}</h3>
                    <Badge variant={MILESTONE_STATUS_VARIANT[milestone.status]}>
                      {MILESTONE_STATUS_LABELS[milestone.status]}
                    </Badge>
                  </div>
                  {milestone.deliverables && (
                    <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">
                      {milestone.deliverables}
                    </p>
                  )}
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    {BILLING_TRIGGER_LABELS[milestone.billingTrigger]}
                    {milestone.plannedEnd && ` · planned ${formatDate(milestone.plannedEnd)}`}
                    {milestone.signedOffAt &&
                      ` · signed off ${formatDate(milestone.signedOffAt)}${milestone.signedOffBy ? ` by ${milestone.signedOffBy}` : ''}`}
                  </p>
                </div>

                <div className="text-right">
                  <div className="tabular font-semibold">
                    {formatMoney(milestone.billingAmount, project.currency)}
                  </div>
                  {milestone.invoice ? (
                    <Link
                      to={`/finance/invoices/${milestone.invoice.id}`}
                      className="text-xs text-primary hover:underline"
                    >
                      {milestone.invoice.number}
                    </Link>
                  ) : (
                    <span className="text-xs text-muted-foreground">Not invoiced</span>
                  )}
                </div>
              </div>

              <div className="mt-3 flex items-center gap-3">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary"
                    style={{ width: `${milestone.percentComplete}%` }}
                  />
                </div>
                <span className="tabular text-xs text-muted-foreground">
                  {milestone.percentComplete}%
                </span>
              </div>

              <Can permission="project.milestone.update">
                {milestone.status !== 'INVOICED' && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {milestone.status === 'PENDING' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setStatus.mutate({ milestoneId: milestone.id, status: 'IN_PROGRESS' })
                        }
                      >
                        Start
                      </Button>
                    )}
                    {milestone.status === 'IN_PROGRESS' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          setStatus.mutate({ milestoneId: milestone.id, status: 'COMPLETED' })
                        }
                      >
                        Mark complete
                      </Button>
                    )}
                    {milestone.status === 'COMPLETED' && (
                      <Button size="sm" onClick={() => setSigningOff(milestone)}>
                        Record client sign-off
                      </Button>
                    )}
                  </div>
                )}
              </Can>
            </CardContent>
          </Card>
        ))
      )}

      {signingOff && (
        <SignOffDialog
          milestone={signingOff}
          projectId={project.id}
          onClose={() => setSigningOff(null)}
        />
      )}
    </div>
  );
}

function SignOffDialog({
  milestone,
  projectId,
  onClose,
}: {
  milestone: Milestone;
  projectId: string;
  onClose: () => void;
}) {
  const setStatus = useSetMilestoneStatus(projectId);
  const [signedOffBy, setSignedOffBy] = useState('');
  const [note, setNote] = useState('');

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record sign-off for {milestone.name}</DialogTitle>
          <DialogDescription>
            Under an on-approval billing trigger, this is what releases the invoice — so it records
            who at the client signed, not just that someone did.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Who signed off</Label>
            <Input
              value={signedOffBy}
              placeholder="Name at the client"
              onChange={(e) => setSignedOffBy(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Note</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={setStatus.isPending || signedOffBy.trim().length < 2}
            onClick={async () => {
              await setStatus.mutateAsync({
                milestoneId: milestone.id,
                status: 'APPROVED',
                signedOffBy,
                note: note || undefined,
              });
              onClose();
            }}
          >
            Record sign-off
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ChangeRequests({ project }: { project: Project }) {
  const save = useSaveChangeRequest(project.id);
  const decide = useDecideChangeRequest(project.id);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    additionalHours: 0,
    additionalCost: '',
    timelineImpactDays: 0,
  });
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const requests = project.changeRequests ?? [];

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex-row items-start justify-between pb-3">
          <div>
            <CardTitle className="text-base">Change requests</CardTitle>
            <CardDescription>
              Approving one moves the contract: the extra money joins the contract value and the
              extra effort joins the budget, so nobody has to remember to do it by hand.
            </CardDescription>
          </div>
          <Can permission="project.project.update">
            <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
              <Plus className="h-4 w-4" />
              Raise
            </Button>
          </Can>
        </CardHeader>
      </Card>

      {adding && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="space-y-1.5">
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>What is changing</Label>
              <Textarea
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label>Extra hours</Label>
                <Input
                  type="number"
                  min={0}
                  value={form.additionalHours}
                  onChange={(e) => setForm({ ...form, additionalHours: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Extra cost</Label>
                <Input
                  inputMode="decimal"
                  placeholder="0.00"
                  value={form.additionalCost}
                  onChange={(e) => setForm({ ...form, additionalCost: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Days added</Label>
                <Input
                  type="number"
                  value={form.timelineImpactDays}
                  onChange={(e) => setForm({ ...form, timelineImpactDays: Number(e.target.value) })}
                />
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={save.isPending || form.title.length < 3 || form.description.length < 3}
                onClick={async () => {
                  await save.mutateAsync({
                    title: form.title,
                    description: form.description,
                    additionalHours: form.additionalHours || undefined,
                    additionalCost: form.additionalCost || undefined,
                    timelineImpactDays: form.timelineImpactDays || undefined,
                  });
                  setAdding(false);
                  setForm({
                    title: '',
                    description: '',
                    additionalHours: 0,
                    additionalCost: '',
                    timelineImpactDays: 0,
                  });
                }}
              >
                Raise change request
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {requests.length === 0 && !adding ? (
        <Card>
          <CardContent className="py-10 text-center">
            <ClipboardList className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Nothing has changed since the contract.</p>
          </CardContent>
        </Card>
      ) : (
        requests.map((request) => (
          <Card key={request.id}>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-muted-foreground">{request.number}</span>
                    <h3 className="font-medium">{request.title}</h3>
                    <Badge
                      variant={
                        request.status === 'APPROVED'
                          ? 'success'
                          : request.status === 'REJECTED'
                            ? 'destructive'
                            : 'secondary'
                      }
                    >
                      {request.status.toLowerCase()}
                    </Badge>
                  </div>
                  <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">
                    {request.description}
                  </p>
                  {request.rejectionReason && (
                    <p className="mt-1.5 text-sm text-destructive">{request.rejectionReason}</p>
                  )}
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    Raised by {request.requestedBy?.name ?? 'someone'} on{' '}
                    {formatDate(request.createdAt)}
                  </p>
                </div>

                <div className="text-right text-sm">
                  <div className="tabular font-semibold">
                    {formatMoney(request.additionalCost, project.currency)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    +{request.additionalHours}h
                    {request.timelineImpactDays !== 0 && `, +${request.timelineImpactDays}d`}
                  </div>
                </div>
              </div>

              {request.status === 'PENDING' && (
                <Can permission="project.milestone.approve">
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      disabled={decide.isPending}
                      onClick={() =>
                        decide.mutate({ changeRequestId: request.id, approve: true })
                      }
                    >
                      <Check className="h-4 w-4" />
                      Approve
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setRejecting(request.id)}>
                      Reject
                    </Button>
                  </div>
                </Can>
              )}

              {rejecting === request.id && (
                <div className="mt-3 space-y-2 rounded-md border p-3">
                  <Label className="text-xs">Why is it being rejected?</Label>
                  <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="destructive"
                      disabled={reason.trim().length < 3}
                      onClick={async () => {
                        await decide.mutateAsync({
                          changeRequestId: request.id,
                          approve: false,
                          reason,
                        });
                        setRejecting(null);
                        setReason('');
                      }}
                    >
                      Reject
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setRejecting(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
}

function Risks({ project }: { project: Project }) {
  const save = useSaveRisk(project.id);
  const { data: users = [] } = useUserOptions();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    description: '',
    category: '',
    probability: 3,
    impact: 3,
    mitigation: '',
    ownerId: '',
  });

  const risks = project.risks ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Can permission="project.project.update">
          <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
            <Plus className="h-4 w-4" />
            Log a risk
          </Button>
        </Can>
      </div>

      {adding && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="space-y-1.5">
              <Label>What could go wrong</Label>
              <Textarea
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-4">
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Input
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Probability (1–5)</Label>
                <Input
                  type="number"
                  min={1}
                  max={5}
                  value={form.probability}
                  onChange={(e) => setForm({ ...form, probability: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Impact (1–5)</Label>
                <Input
                  type="number"
                  min={1}
                  max={5}
                  value={form.impact}
                  onChange={(e) => setForm({ ...form, impact: Number(e.target.value) })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Owner</Label>
                <Select
                  value={form.ownerId}
                  onChange={(e) => setForm({ ...form, ownerId: e.target.value })}
                >
                  <option value="">Nobody yet</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>How it is being mitigated</Label>
              <Textarea
                rows={2}
                value={form.mitigation}
                onChange={(e) => setForm({ ...form, mitigation: e.target.value })}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Severity is probability × impact, worked out here rather than typed — two people
              scoring the same risk the same way must land on the same number.
            </p>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={save.isPending || form.description.trim().length < 3}
                onClick={async () => {
                  await save.mutateAsync({
                    description: form.description,
                    category: form.category || undefined,
                    probability: form.probability,
                    impact: form.impact,
                    mitigation: form.mitigation || undefined,
                    ownerId: form.ownerId || undefined,
                  });
                  setAdding(false);
                  setForm({
                    description: '',
                    category: '',
                    probability: 3,
                    impact: 3,
                    mitigation: '',
                    ownerId: '',
                  });
                }}
              >
                Log risk
              </Button>
              <Button size="sm" variant="outline" onClick={() => setAdding(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {risks.length === 0 && !adding ? (
        <Card>
          <CardContent className="py-10 text-center">
            <ShieldAlert className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              No risks logged. That is either good news or an oversight.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="divide-y p-0">
            {risks.map((risk) => (
              <div key={risk.id} className="flex items-start gap-4 p-4">
                <span
                  className={cn(
                    'tabular flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-bold',
                    risk.severity >= 15
                      ? 'bg-red-100 text-red-700'
                      : risk.severity >= 8
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-muted text-muted-foreground',
                  )}
                  title={`Probability ${risk.probability} × impact ${risk.impact}`}
                >
                  {risk.severity}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{risk.description}</p>
                  {risk.mitigation && (
                    <p className="mt-0.5 text-sm text-muted-foreground">{risk.mitigation}</p>
                  )}
                  <p className="mt-1 text-xs text-muted-foreground">
                    {RISK_STATUS_LABELS[risk.status]}
                    {risk.category && ` · ${risk.category}`}
                    {risk.owner && ` · ${risk.owner.name}`}
                  </p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Team({ project }: { project: Project }) {
  const setMembers = useSetProjectMembers();
  const { data: users = [] } = useUserOptions();
  const [members, setLocal] = useState(
    project.members.map((member) => ({
      userId: member.user.id,
      allocationPercent: member.allocationPercent,
      role: member.role ?? '',
    })),
  );
  const [error, setError] = useState<string | null>(null);

  const available = users.filter((user) => !members.some((member) => member.userId === user.id));

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Who is on this project</CardTitle>
        <CardDescription>
          Allocation is the share of someone&rsquo;s week committed here. It is what a resourcing
          view adds up to decide whether anyone is over-committed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {members.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">Nobody assigned yet.</p>
        ) : (
          members.map((member, index) => {
            const user = users.find((candidate) => candidate.id === member.userId);
            return (
              <div key={member.userId} className="flex flex-wrap items-center gap-3 rounded-lg border p-3">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium">{user?.name ?? 'Unknown'}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {user?.designation ?? user?.email}
                  </div>
                </div>
                <Input
                  className="h-8 w-32"
                  placeholder="Role"
                  value={member.role}
                  onChange={(e) => {
                    const next = [...members];
                    next[index] = { ...member, role: e.target.value };
                    setLocal(next);
                  }}
                />
                <div className="flex items-center gap-1">
                  <Input
                    className="tabular h-8 w-16"
                    type="number"
                    min={1}
                    max={100}
                    value={member.allocationPercent}
                    onChange={(e) => {
                      const next = [...members];
                      next[index] = { ...member, allocationPercent: Number(e.target.value) };
                      setLocal(next);
                    }}
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setLocal(members.filter((_, i) => i !== index))}
                >
                  Remove
                </Button>
              </div>
            );
          })
        )}

        <Can permission="project.project.update">
          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Select
              className="h-9 w-auto min-w-[12rem]"
              value=""
              onChange={(e) => {
                if (!e.target.value) return;
                setLocal([...members, { userId: e.target.value, allocationPercent: 100, role: '' }]);
              }}
            >
              <option value="">Add someone…</option>
              {available.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </Select>
            <Button
              size="sm"
              disabled={setMembers.isPending}
              onClick={async () => {
                setError(null);
                try {
                  await setMembers.mutateAsync({
                    id: project.id,
                    members: members.map((member) => ({
                      userId: member.userId,
                      allocationPercent: member.allocationPercent,
                      role: member.role || undefined,
                    })),
                  });
                } catch (caught) {
                  setError(
                    caught instanceof ApiRequestError ? caught.message : 'Could not save the team.',
                  );
                }
              }}
            >
              <Users className="h-4 w-4" />
              {setMembers.isPending ? 'Saving…' : 'Save team'}
            </Button>
          </div>
        </Can>

        <FormError message={error} />
      </CardContent>
    </Card>
  );
}

function Money({ project }: { project: Project }) {
  const { can } = usePermission();
  const allowed = can('finance.cost.view');
  const { data, isLoading } = useProfitability(project.id, allowed);

  if (!allowed) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Profitability shows cost rates, which are salary-adjacent. It needs the
          &ldquo;see cost rates and margin&rdquo; permission.
        </CardContent>
      </Card>
    );
  }

  if (isLoading) return <TableSkeleton columns={3} />;
  if (!data) return null;

  return (
    <div className="space-y-5">
      {data.alert && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {data.alert} — {data.loggedHours}h logged against a {data.budgetHours}h budget.
          </span>
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Contracted" value={formatMoney(data.contractValue, data.currency)} />
        <Stat
          label="Invoiced"
          value={formatMoney(data.invoiced, data.currency)}
          hint={`${formatMoney(data.notYetInvoiced, data.currency)} still to bill`}
        />
        <Stat
          label="Collected"
          value={formatMoney(data.collected, data.currency)}
          hint={`${formatMoney(data.outstanding, data.currency)} outstanding`}
        />
        <Stat
          label="Cost of delivery"
          value={formatMoney(data.actualCost, data.currency)}
          hint={
            data.marginPercent === null
              ? 'No margin until something is billed'
              : `${data.marginPercent}% margin on what is billed`
          }
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Budget against actual</CardTitle>
            <CardDescription>
              Cost uses the rate stored on each time entry, not today&rsquo;s rate — a raise in
              March does not rewrite what January cost.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Meter
              label="Hours"
              used={data.loggedHours}
              total={data.budgetHours}
              percent={data.budgetConsumptionPercent}
              suffix="h"
            />
            <div className="grid grid-cols-3 gap-3 text-sm">
              <Mini label="Billable" value={`${data.billableHours}h`} />
              <Mini label="Non-billable" value={`${data.nonBillableHours}h`} />
              <Mini label="Remaining" value={`${data.remainingHours}h`} />
            </div>
            <div className="border-t pt-3">
              <Row
                label="Value of billable time"
                value={formatMoney(data.billableValue, data.currency)}
              />
              <Row
                label="Margin against contract"
                value={
                  data.contractMarginPercent === null ? '—' : `${data.contractMarginPercent}%`
                }
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Where the hours went</CardTitle>
          </CardHeader>
          <CardContent>
            {data.byPerson.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                No time logged yet.
              </p>
            ) : (
              <ul className="space-y-2.5">
                {data.byPerson.map((person) => (
                  <li key={person.userId} className="flex items-center justify-between text-sm">
                    <span>{person.name}</span>
                    <span className="tabular text-muted-foreground">
                      {person.hours}h · {formatMoney(person.cost, data.currency)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {data.invoices.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Invoices raised on this project</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {data.invoices.map((invoice) => (
                <li key={invoice.id} className="flex items-center justify-between text-sm">
                  <Link
                    to={`/finance/invoices/${invoice.id}`}
                    className="text-primary hover:underline"
                  >
                    {invoice.number}
                  </Link>
                  <span className="tabular text-muted-foreground">
                    {formatMoney(invoice.total, data.currency)} · paid{' '}
                    {formatMoney(invoice.paid, data.currency)}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function CloseDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const close = useCloseProject();
  const [checklist, setChecklist] = useState<Record<string, boolean>>(
    project.closureChecklist ?? {},
  );
  const [error, setError] = useState<string | null>(null);

  const outstanding = CLOSURE_ITEMS.filter(([key]) => !checklist[key]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Close {project.name}</DialogTitle>
          <DialogDescription>
            Every box has to be ticked, and every work item closed or moved out. A project that
            closes with loose ends leaves them for someone to find later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {CLOSURE_ITEMS.map(([key, label]) => (
            <label key={key} className="flex cursor-pointer items-center gap-2.5 rounded-md border p-3">
              <Checkbox
                checked={Boolean(checklist[key])}
                onCheckedChange={(value) => setChecklist({ ...checklist, [key]: value === true })}
              />
              <span className="text-sm">{label}</span>
            </label>
          ))}
          <FormError message={error} />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Not yet
          </Button>
          <Button
            disabled={close.isPending || outstanding.length > 0}
            onClick={async () => {
              setError(null);
              try {
                await close.mutateAsync({ id: project.id, checklist });
                onClose();
              } catch (caught) {
                setError(
                  caught instanceof ApiRequestError
                    ? caught.message
                    : 'Could not close the project.',
                );
              }
            }}
          >
            {outstanding.length > 0
              ? `${outstanding.length} still outstanding`
              : close.isPending
                ? 'Closing…'
                : 'Close project'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Small pieces ─────────────────────────────────────────────────────────────

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="tabular mt-1 text-xl font-bold tracking-tight">{value}</p>
        {hint && <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}

function Row({
  label,
  value,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  tone?: 'warning';
}) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn('font-medium', tone === 'warning' && 'text-amber-600')}>{value}</span>
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border p-2.5">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="tabular mt-0.5 font-semibold">{value}</div>
    </div>
  );
}

function Meter({
  label,
  used,
  total,
  percent,
  suffix,
}: {
  label: string;
  used: number;
  total: number;
  percent: number | null;
  suffix: string;
}) {
  const width = percent === null ? 0 : Math.min(100, percent);
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted-foreground">{label}</span>
        <span className="tabular">
          {used}
          {suffix} of {total}
          {suffix}
          {percent !== null && <span className="ml-1.5 text-muted-foreground">({percent}%)</span>}
        </span>
      </div>
      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-muted">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            percent !== null && percent >= 100
              ? 'bg-destructive'
              : percent !== null && percent >= 80
                ? 'bg-amber-500'
                : 'bg-primary',
          )}
          style={{ width: `${width}%` }}
        />
      </div>
    </div>
  );
}
